import React, { useEffect, useState } from 'react';
import { ArrowLeft, CheckCircle, Save } from 'lucide-react';

const COLORS = {
  navyBlue: '#003366',
  orange: '#FF6B35',
  mustard: '#FFAA00',
  green: '#16A34A',
  red: '#DC2626',
  lightGray: '#F9FAFB',
  white: '#FFFFFF',
  borderGray: '#D1D5DB',
  textGray: '#6B7280',
  textDark: '#1F2937',
};

// Areas match the codes nwa_screening_classification.py already uses for
// the legacy sheet, so analysis written against those codes keeps working.
const AREAS = [
  { key: 'reading', label: 'Reading', emoji: '📖', bg: '#FAF5FF', border: '#E9D5FF', iconBg: '#F3E8FF', iconColor: '#9333EA' },
  { key: 'writing', label: 'Writing', emoji: '✍️', bg: '#EFF6FF', border: '#BFDBFE', iconBg: '#DBEAFE', iconColor: '#2563EB' },
  { key: 'spelling', label: 'Spelling', emoji: '🔤', bg: '#FFF7ED', border: '#FDBA74', iconBg: '#FFEDD5', iconColor: '#EA580C' },
  { key: 'maths', label: 'Maths', emoji: '🔢', bg: '#F0FDF4', border: '#86EFAC', iconBg: '#DCFCE7', iconColor: '#16A34A' },
  { key: 'science', label: 'Science', emoji: '🔬', bg: '#F0FDFA', border: '#99F6E4', iconBg: '#CCFBF1', iconColor: '#0D9488' },
  { key: 'learning_new_languages', label: 'Learning New Languages', emoji: '🌍', bg: '#FEF3C7', border: '#FCD34D', iconBg: '#FEF9C3', iconColor: '#CA8A04' },
  { key: 'speaking_and_listening', label: 'Speaking & Listening', emoji: '🗣️', bg: '#FDF2F8', border: '#FBCFE8', iconBg: '#FCE7F3', iconColor: '#DB2777' },
  { key: 'ict_computing', label: 'ICT / Computing', emoji: '💻', bg: '#EFF6FF', border: '#BFDBFE', iconBg: '#DBEAFE', iconColor: '#2563EB' },
  { key: 'music', label: 'Music', emoji: '🎵', bg: '#FAF5FF', border: '#E9D5FF', iconBg: '#F3E8FF', iconColor: '#9333EA' },
  { key: 'art_creativity', label: 'Art / Creativity', emoji: '🎨', bg: '#FFF7ED', border: '#FDBA74', iconBg: '#FFEDD5', iconColor: '#EA580C' },
  { key: 'pe_physical_confidence', label: 'PE / Physical', emoji: '🏃', bg: '#F0FDF4', border: '#86EFAC', iconBg: '#DCFCE7', iconColor: '#16A34A' },
];

const fetchWithTimeout = (url, options = {}, timeoutMs = 30000) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const token = window.sessionStorage.getItem('nwaAccessToken');
  const headers = { ...(options.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetch(url, { ...options, headers, signal: controller.signal }).finally(() => clearTimeout(timer));
};

const todayDDMMYYYY = () => {
  const d = new Date();
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
};

const styles = {
  container: { minHeight: '100vh', backgroundColor: COLORS.lightGray },
  header: {
    backgroundColor: COLORS.navyBlue,
    color: COLORS.white,
    padding: '8px 16px',
    borderBottom: `2px solid ${COLORS.mustard}`,
  },
  headerContent: {
    maxWidth: '900px',
    margin: '0 auto',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  backButton: {
    backgroundColor: 'transparent',
    border: 'none',
    color: COLORS.white,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    padding: '8px 0',
  },
  body: { maxWidth: '900px', margin: '0 auto', padding: '24px' },
  card: {
    backgroundColor: COLORS.white,
    borderRadius: '8px',
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
    padding: '24px',
    marginBottom: '20px',
  },
  label: { display: 'block', fontSize: '14px', fontWeight: '600', color: COLORS.textDark, marginBottom: '6px' },
  input: {
    width: '100%',
    padding: '10px 14px',
    border: `2px solid ${COLORS.borderGray}`,
    borderRadius: '6px',
    fontSize: '15px',
    boxSizing: 'border-box',
    marginBottom: '16px',
  },
  select: {
    width: '100%',
    padding: '10px 14px',
    border: `2px solid ${COLORS.borderGray}`,
    borderRadius: '6px',
    fontSize: '15px',
    backgroundColor: COLORS.white,
    boxSizing: 'border-box',
    marginBottom: '16px',
  },
  button: {
    backgroundColor: COLORS.orange,
    color: COLORS.white,
    padding: '12px 24px',
    borderRadius: '6px',
    fontWeight: '600',
    border: 'none',
    cursor: 'pointer',
    fontSize: '16px',
  },
  errorBox: {
    backgroundColor: '#FEE2E2',
    border: '1px solid #FCA5A5',
    color: '#991B1B',
    padding: '12px 16px',
    borderRadius: '6px',
    marginBottom: '16px',
  },
};

/**
 * Props: student, apiUrl, facilitatorId, facilitatorName, schoolId, schoolName, programmeId,
 * programmeName, onBack, onSaved. Same shape as Hast2Screening/NumeracyScreening.
 */
export default function ConfidenceScreening({ student, apiUrl, facilitatorId, facilitatorName, schoolId, schoolName, programmeId, programmeName, initialTimepoint, correctionMode = false, onBack, onSaved }) {
  const [stage, setStage] = useState('setup'); // setup | complete
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [screeningDate, setScreeningDate] = useState(todayDDMMYYYY());
  // Timepoint is chosen on the dashboard's Timeline card before this
  // screen is ever reached, so it's fixed for the whole session, not
  // editable here.
  const timepoint = initialTimepoint || 'Baseline';
  const [administrationMode, setAdministrationMode] = useState('Individual');
  const administrationBatchId = ''; // kept blank internally; no practitioner entry needed
  const [assessorName, setAssessorName] = useState(facilitatorName || '');
  const [notes, setNotes] = useState('');
  const [ratings, setRatings] = useState({});
  const [result, setResult] = useState(null);
  const [correctionLoading, setCorrectionLoading] = useState(false);
  const [correctionLoaded, setCorrectionLoaded] = useState(true);
  const [effectiveCorrectionMode, setEffectiveCorrectionMode] = useState(false);

  useEffect(() => {
    if (!student?.student_code) return;
    let cancelled = false;
    setCorrectionLoading(true);
    setCorrectionLoaded(false);
    setEffectiveCorrectionMode(false);
    setError('');

    fetchWithTimeout(`${apiUrl}/api/confidence/record/${student.student_code}?timepoint=${encodeURIComponent(timepoint)}`)
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (res.status === 404) return { noRecord: true };
        if (!res.ok) throw new Error(data.error || 'Could not load the saved Confidence Levels record');
        return data;
      })
      .then((data) => {
        if (cancelled) return;
        if (data?.noRecord) {
          // No previous administration exists. This is a genuine new entry, even if
          // the wider screening package has been reopened to add this screener.
          setCorrectionLoaded(true);
          return;
        }

        if (!data?.correction_allowed) {
          // A saved record exists, but the server has not authorised this screener
          // for correction. Keep it protected rather than showing an editable blank form.
          setCorrectionLoaded(false);
          setError('Confidence Levels is already saved and locked. Reopen this screener for correction before editing it.');
          return;
        }

        const saved = data.ratings || {};
        const nextRatings = {};
        AREAS.forEach(({ key }) => {
          const value = saved[key];
          if (value === '' || value === null || value === undefined) {
            throw new Error('The saved Confidence ratings could not be loaded completely');
          }
          nextRatings[key] = String(value);
        });
        setRatings(nextRatings);
        if (data.screening_date) setScreeningDate(data.screening_date);
        if (data.administration_mode) setAdministrationMode(data.administration_mode);
        if (data.assessor_name) setAssessorName(data.assessor_name);
        setNotes(data.notes || '');
        setEffectiveCorrectionMode(true);
        setCorrectionLoaded(true);
      })
      .catch((e) => {
        if (!cancelled) {
          setCorrectionLoaded(false);
          setEffectiveCorrectionMode(false);
          setError(e.message || 'Could not load the saved Confidence Levels record');
        }
      })
      .finally(() => { if (!cancelled) setCorrectionLoading(false); });
    return () => { cancelled = true; };
  }, [student?.student_code, apiUrl, timepoint, correctionMode]);

  const allRated = AREAS.every((a) => ratings[a.key] && Number(ratings[a.key]) >= 1 && Number(ratings[a.key]) <= 5);

  const setRating = (key, value) => {
    if (value === '' || (Number(value) >= 1 && Number(value) <= 5)) {
      setRatings((prev) => ({ ...prev, [key]: value }));
    }
  };

  const submit = async () => {
    if (!allRated) {
      setError('Please rate every area from 1 to 5 before saving.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const ratingsPayload = {};
      AREAS.forEach((a) => { ratingsPayload[a.key] = Number(ratings[a.key]); });

      const res = await fetchWithTimeout(`${apiUrl}/api/confidence/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          student_code: student.student_code,
          screening_date: screeningDate,
          timepoint,
          school_id: schoolId || student?.school_id || '',
          school_name: schoolName,
          programme_id: programmeId || student?.programme_id || '',
          programme_name: programmeName,
          assessor_name: assessorName,
          administration_mode: administrationMode,
          administration_batch_id: administrationBatchId,
          entered_by_user_id: facilitatorId,
          entered_by_name: facilitatorName,
          notes,
          correction_mode: effectiveCorrectionMode,
          ratings: ratingsPayload,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not save this administration');
      setResult(data);
      setStage('complete');
      if (onSaved) onSaved(data);
    } catch (e) {
      setError(e.message || 'Could not save this administration');
    } finally {
      setLoading(false);
    }
  };

  const Header = ({ title }) => (
    <div style={styles.header}>
      <div style={styles.headerContent}>
        <button style={styles.backButton} onClick={onBack}>
          <ArrowLeft size={18} />
          Back to {student.full_name}
        </button>
        <span style={{ fontSize: '13px', color: COLORS.mustard, fontWeight: '600' }}>{title}</span>
      </div>
    </div>
  );

  if (stage === 'complete') {
    return (
      <div style={styles.container}>
        <Header title="Confidence Levels: Saved" />
        <div style={styles.body}>
          <div style={styles.card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: COLORS.green, marginBottom: '16px' }}>
              <CheckCircle size={28} />
              <h2 style={{ fontSize: '20px', fontWeight: 'bold' }}>Saved</h2>
            </div>
            {result && result.average_percentage != null && (
              <p style={{ color: COLORS.textDark, marginBottom: '20px' }}><strong>Average confidence:</strong> {result.average_percentage}%</p>
            )}
            <button style={{ ...styles.button, backgroundColor: COLORS.white, color: COLORS.navyBlue, border: `2px solid ${COLORS.navyBlue}` }} onClick={onBack}>
              Back to {student.full_name}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <Header title="Confidence Levels" />
      <div style={styles.body}>
        <div style={styles.card}>
          <h1 style={{ fontSize: '22px', fontWeight: 'bold', color: COLORS.navyBlue, marginBottom: '20px' }}>
            Confidence Levels — {student.full_name}
          </h1>

          {error && <div style={styles.errorBox}>{error}</div>}
          {effectiveCorrectionMode && !error && (
            <div style={{ backgroundColor: '#FFF7ED', border: '1px solid #FED7AA', color: '#8A5A2B', padding: '10px 14px', borderRadius: '6px', marginBottom: '16px', textAlign: 'center' }}>
              {correctionLoading ? 'Loading the previously saved Confidence Levels values...' : 'Correction mode - previous values loaded. Change only what is needed, then save the correction.'}
            </div>
          )}

          <label style={styles.label}>Screening date (DD/MM/YYYY)</label>
          <input
            style={styles.input}
            type="text"
            value={screeningDate}
            onChange={(e) => setScreeningDate(e.target.value)}
            placeholder="DD/MM/YYYY"
          />

          <label style={styles.label}>Administration mode</label>
          <select
            style={styles.select}
            value={administrationMode}
            onChange={(e) => setAdministrationMode(e.target.value)}
          >
            <option value="Individual">Individual (1:1)</option>
            <option value="Group">Group</option>
          </select>

          <label style={styles.label}>Assessor name</label>
          <input
            style={styles.input}
            type="text"
            value={assessorName}
            onChange={(e) => setAssessorName(e.target.value)}
          />

          <div style={{
            textAlign: 'center',
            color: COLORS.textGray,
            fontSize: '13px',
            fontStyle: 'italic',
            backgroundColor: '#FFFBEB',
            border: '1px solid #FCD34D',
            borderRadius: '8px',
            padding: '10px 14px',
            marginBottom: '16px',
          }}>
            <strong style={{ color: COLORS.navyBlue }}>Confidence Rating Scale:</strong>{' '}
            😣 1 (Very Low) &nbsp; 😟 2 (Low) &nbsp; 😐 3 (Moderate) &nbsp; 🙂 4 (Good) &nbsp; 😊 5 (Very Strong)
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px', marginBottom: '8px' }}>
            {AREAS.map(({ key, label, emoji, bg, border, iconBg, iconColor }) => (
              <div key={key} style={{ backgroundColor: bg, padding: '16px', borderRadius: '8px', border: `1px solid ${border}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                  <div style={{
                    fontSize: '24px', color: iconColor, backgroundColor: iconBg,
                    width: '44px', height: '44px', borderRadius: '8px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {emoji}
                  </div>
                  <label style={{ fontSize: '14px', fontWeight: '600', color: COLORS.navyBlue }}>
                    {label} *
                  </label>
                </div>
                <input
                  type="number"
                  min="1"
                  max="5"
                  value={ratings[key] || ''}
                  onChange={(e) => setRating(key, e.target.value)}
                  style={{ ...styles.input, fontSize: '14px', padding: '8px', marginBottom: 0 }}
                  placeholder="1-5"
                  autoComplete="off"
                />
              </div>
            ))}
          </div>

          <label style={{ ...styles.label, marginTop: '16px' }}>Recommendations / notes (optional)</label>
          <textarea
            style={{ ...styles.input, minHeight: '80px', fontFamily: 'inherit' }}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />

          <button
            style={{ ...styles.button, display: 'flex', alignItems: 'center', gap: '8px' }}
            onClick={submit}
            disabled={loading || correctionLoading || !correctionLoaded || !allRated}
          >
            <Save size={18} /> {loading ? 'Saving…' : (effectiveCorrectionMode ? 'Save Correction' : 'Submit & Lock')}
          </button>
        </div>
      </div>
    </div>
  );
}
